import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import styled from "styled-components";

// Icon Components
const CameraIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3H15L17 5H21C21.5304 5 22.0391 5.21071 22.4142 5.58579C22.7893 5.96086 23 6.46957 23 7V19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V7C1 6.46957 1.21071 5.96086 1.58579 5.58579C1.96086 5.21071 2.46957 5 3 5H7L9 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BrainIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C13.6569 2 15 3.34315 15 5C15 5.35064 14.9398 5.68722 14.829 6C16.3889 6.26375 17.5 7.61536 17.5 9.25C17.5 9.44731 17.4851 9.64095 17.4566 9.82918C18.9423 10.2149 20 11.5578 20 13.1667C20 14.9553 18.4553 16.5 16.6667 16.5H7.33333C5.54467 16.5 4 14.9553 4 13.1667C4 11.5578 5.05774 10.2149 6.54341 9.82918C6.51494 9.64095 6.5 9.44731 6.5 9.25C6.5 7.61536 7.61107 6.26375 9.17102 6C9.06019 5.68722 9 5.35064 9 5C9 3.34315 10.3431 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.5 11L10.5 12L14.5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ZapIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon points="13,2 3,14 12,14 11,22 21,10 12,10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.1"/>
  </svg>
);

const UserIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 11C13.1046 11 14 10.1046 14 9C14 7.89543 13.1046 7 12 7C10.8954 7 10 7.89543 10 9C10 10.1046 10.8954 11 12 11Z" fill="currentColor" fillOpacity="0.1"/>
    <circle cx="18" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.1"/>
    <path d="M9 12L11 14L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DevicesIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.05"/>
    <path d="M8 21H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M12 17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="14" y="8" width="6" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
    <circle cx="17" cy="15" r="0.5" fill="currentColor"/>
  </svg>
);

// Styled components with app's color scheme
const LandingRoot = styled.div`
  font-family: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #ffffff;
  color: #2c2c2c;
  min-height: 100vh;
`;

const Navbar = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(141, 79, 27, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 70px; /* Fixed height prevents expansion */
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 1rem;
    height: 65px; /* Increased from 60px to 65px to accommodate larger mobile logo */
    min-height: 65px; /* Ensure minimum height */
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 1.5rem;
  color: #8d4f1b;
  flex-shrink: 0; /* Prevent logo from shrinking */
  
  @media (max-width: 768px) {
    /* Ensure proper spacing and alignment on mobile */
    min-width: fit-content;
    /* Optimize for crisp SVG rendering on mobile */
    -webkit-transform: translate3d(0, 0, 0); /* Better than translateZ for SVG */
    transform: translate3d(0, 0, 0);
    /* Prevent subpixel positioning */
    position: relative;
    /* Ensure container doesn't interfere with SVG rendering */
    isolation: isolate;
  }
`;

const LogoIcon = styled.img`
  height: 65px;
  width: auto;
  max-width: none;
  object-fit: contain;
  image-rendering: auto;
  shape-rendering: geometricPrecision;
  transform: scale(1);
  filter: contrast(1.1) brightness(1.05);
  
  @media (max-width: 768px) {
    height: 60px;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    shape-rendering: crispEdges;
    filter: none;
    transform: translate3d(0, 0, 0) scale(1);
    backface-visibility: hidden;
    -webkit-font-smoothing: antialiased;
    -webkit-backface-visibility: hidden;
  }
  
  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    image-rendering: auto;
    shape-rendering: geometricPrecision;
    filter: none;
  }
  
  @media (max-width: 768px) and (-webkit-min-device-pixel-ratio: 2) {
    height: 60px;
    image-rendering: auto;
    shape-rendering: geometricPrecision;
    filter: none;
    transform: translate3d(0, 0, 0);
    will-change: auto;
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  align-items: center;
  gap: 2rem;
  margin: 0;
  padding: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #8d4f1b;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileSideNav = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-300px'};
  width: 300px;
  height: 100vh;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  transition: right 0.3s ease;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileCloseButton = styled.button`
  align-self: flex-end;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #8d4f1b;
  cursor: pointer;
  padding: 0.5rem;
  margin-bottom: 1rem;
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MobileNavLink = styled.a`
  text-decoration: none;
  color: #2c2c2c;
  font-weight: 500;
  font-size: 1.1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(141, 79, 27, 0.1);
  transition: color 0.3s ease;
  
  &:hover {
    color: #8d4f1b;
  }
`;

const MobileButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #2c2c2c;
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: #8d4f1b;
  }
`;

const LoginButton = styled.button`
  background: transparent;
  border: 2px solid #8d4f1b;
  color: #8d4f1b;
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #8d4f1b;
    color: white;
  }
`;

const GetStartedButton = styled.button`
  background: linear-gradient(135deg, #8d4f1b, #b8651f);
  border: none;
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const HeroSection = styled.section`
  padding: 140px 2rem 80px; /* Increased top padding to account for fixed navbar height */
  background: linear-gradient(135deg, #faf8f6 0%, #f5f2ee 100%);
  min-height: 80vh;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 125px 2rem 80px; /* Increased from 120px to 125px for new mobile navbar height */
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const HeroText = styled.div`
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    color: #2c2c2c;
    
    .highlight {
      color: #8d4f1b;
    }
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    color: #666;
    margin-bottom: 2rem;
  }
`;

const HeroImage = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(141, 79, 27, 0.15);
  }
`;

const Section = styled.section`
  padding: 120px 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -50%;
    width: 200%;
    height: 100%;
    background: linear-gradient(45deg, transparent 45%, rgba(141, 79, 27, 0.02) 50%, transparent 55%);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: -20%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(141, 79, 27, 0.05) 0%, transparent 70%);
    border-radius: 50%;
    transform: translateY(-50%);
    z-index: 0;
  }
  
  h2 {
    text-align: center;
    font-size: 3.5rem;
    font-weight: 800;
    color: #2c2c2c;
    margin-bottom: 1rem;
    position: relative;
    z-index: 1;
    background: linear-gradient(135deg, #2c2c2c 0%, #8d4f1b 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
`;

const FeaturesSubtitle = styled.p`
  text-align: center;
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 1;
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(135deg, #8d4f1b, #b8651f);
    margin: 1.5rem auto 0;
    border-radius: 2px;
  }
`;

const FeaturesContainer = styled.div`
  position: relative;
  z-index: 1;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
  
  .floating-circle {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(141, 79, 27, 0.03), rgba(184, 101, 31, 0.03));
    animation: float 6s ease-in-out infinite;
    
    &:nth-child(1) {
      width: 100px;
      height: 100px;
      top: 10%;
      left: 10%;
      animation-delay: 0s;
    }
    
    &:nth-child(2) {
      width: 60px;
      height: 60px;
      top: 60%;
      right: 15%;
      animation-delay: 2s;
    }
    
    &:nth-child(3) {
      width: 80px;
      height: 80px;
      bottom: 20%;
      left: 20%;
      animation-delay: 4s;
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) rotate(0deg);
    }
    50% {
      transform: translateY(-20px) rotate(180deg);
    }
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2.5rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: 420px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FeatureCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%);
  backdrop-filter: blur(20px);
  padding: 3rem 2.5rem;
  border-radius: 24px;
  border: 1px solid rgba(141, 79, 27, 0.1);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 8px 16px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  transform: translateY(0);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(141, 79, 27, 0.1), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 30px 60px rgba(0, 0, 0, 0.15),
      0 12px 24px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border-color: rgba(141, 79, 27, 0.2);
    
    &::before {
      left: 100%;
    }
    
    .icon-container {
      transform: scale(1.1) rotate(5deg);
      background: linear-gradient(135deg, #8d4f1b, #b8651f);
      color: white;
      
      svg {
        color: white;
        filter: none;
      }
    }
    
    .feature-number {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .feature-number {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #8d4f1b, #b8651f);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 700;
    opacity: 0.7;
    transform: scale(0.9);
    transition: all 0.3s ease;
  }
  
  .icon-container {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, rgba(141, 79, 27, 0.1), rgba(184, 101, 31, 0.1));
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 2rem;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    color: #8d4f1b;
    
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.3) 50%, transparent 60%);
      transform: rotate(45deg);
      transition: transform 0.6s ease;
    }
    
    svg {
      transition: all 0.4s ease;
      z-index: 1;
      position: relative;
    }
  }
  
  &:hover .icon-container::after {
    transform: rotate(45deg) translate(100%, 100%);
  }
  
  h3 {
    color: #2c2c2c;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    transition: color 0.3s ease;
  }
  
  p {
    color: #666;
    line-height: 1.7;
    font-size: 1rem;
    transition: color 0.3s ease;
  }
  
  &:hover h3 {
    color: #8d4f1b;
  }
  
  &:hover p {
    color: #555;
  }
`;

const TestimonialSection = styled.section`
  background: linear-gradient(135deg, #8d4f1b, #b8651f);
  padding: 80px 2rem;
  text-align: center;
  
  h2 {
    color: white;
    font-size: 2.5rem;
    margin-bottom: 3rem;
  }
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 3rem;
  border-radius: 20px;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
`;

const TestimonialAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1.5rem;
`;

const TestimonialText = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #2c2c2c;
  font-style: italic;
  margin-bottom: 1.5rem;
`;

const TestimonialAuthor = styled.div`
  color: #8d4f1b;
  font-weight: 600;
`;

const CTASection = styled.section`
  background: #f8f9fa;
  padding: 80px 2rem;
  text-align: center;
  
  h2 {
    font-size: 2.5rem;
    color: #2c2c2c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.25rem;
    color: #666;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #8d4f1b, #b8651f);
  border: none;
  color: white;
  padding: 1rem 3rem;
  border-radius: 30px;
  font-size: 1.25rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
  }
`;

const Footer = styled.footer`
  background: #2c2c2c;
  color: white;
  padding: 60px 2rem 20px;
  
  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
    margin-bottom: 2rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
  
  .footer-section {
    h4 {
      color: #8d4f1b;
      margin-bottom: 1rem;
      font-size: 1.25rem;
    }
    
    p {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
    
    a {
      color: #ccc;
      text-decoration: none;
      transition: color 0.3s ease;
      
      &:hover {
        color: #8d4f1b;
      }
    }
  }
  
  .footer-bottom {
    text-align: center;
    padding-top: 2rem;
    border-top: 1px solid #444;
    color: #ccc;
  }
`;

// Testimonials data
const testimonials = [
  {
    name: "Dr. Amina Hassan",
    role: "Dermatologist, Lagos University Teaching Hospital",
    text: "DermAfricans has revolutionized how I diagnose skin conditions. The AI accuracy on African skin tones is remarkable, and it's helped me catch conditions I might have missed.",
    avatar: "https://images.unsplash.com/photo-1594824475520-b5b23e1e0c7e?auto=format&fit=crop&w=150&q=80"
  },
  {
    name: "Dr. Kwame Osei",
    role: "General Practitioner, Accra Medical Center",
    text: "As a GP in rural Ghana, DermAfricans gives me the confidence to handle dermatological cases that I would have had to refer. It's like having a dermatologist in my pocket.",
    avatar: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=150&q=80"
  },
  {
    name: "Dr. Fatima Al-Rashid",
    role: "Head of Dermatology, Cairo Medical College",
    text: "The training data's focus on African and Middle Eastern skin types makes this tool invaluable for our region. My residents use it as a learning tool and diagnostic aid.",
    avatar: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=150&q=80"
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Hero animation
    if (heroRef.current) {
      gsap.fromTo(heroRef.current, 
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
      );
    }

    // Features animation with fallback
    if (featuresRef.current) {
      const featureCards = featuresRef.current.querySelectorAll('[data-feature-card]');
      
      if (featureCards.length > 0) {
        // Set initial state and animate
        gsap.set(featureCards, { opacity: 0, y: 60, scale: 0.9 });
        
        // Create timeline for staggered animations
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: featuresRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        });
        
        tl.to(featureCards, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(1.7)"
        });
        
        // Add individual hover animations and floating background elements
        featureCards.forEach((card, index) => {
          const iconContainer = card.querySelector('.icon-container');
          const featureNumber = card.querySelector('.feature-number');
          
          if (iconContainer && featureNumber) {
            // Floating animation for icons
            gsap.to(iconContainer, {
              y: -5,
              duration: 2,
              repeat: -1,
              yoyo: true,
              ease: "power2.inOut",
              delay: index * 0.2
            });
            
            // Pulse animation for numbers
            gsap.to(featureNumber, {
              scale: 1.1,
              duration: 1.5,
              repeat: -1,
              yoyo: true,
              ease: "power2.inOut",
              delay: index * 0.3
            });
          }
        });
        
        // Animate floating background elements
        const floatingElements = featuresRef.current.querySelectorAll('.floating-circle');
        floatingElements.forEach((element, index) => {
          gsap.to(element, {
            y: -30,
            x: 10,
            rotation: 360,
            duration: 8 + index * 2,
            repeat: -1,
            yoyo: true,
            ease: "power1.inOut",
            delay: index * 1.5
          });
        });
        
        // Fallback: ensure cards are visible after a short delay
        setTimeout(() => {
          featureCards.forEach(card => {
            if (card instanceof HTMLElement) {
              card.style.opacity = '1';
              card.style.transform = 'translateY(0) scale(1)';
            }
          });
        }, 1500);
      }
    }

    // Testimonial rotation
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);

    // Handle window resize to close mobile menu on desktop
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
      // Clean up any GSAP animations
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileNavClick = (href: string) => {
    closeMobileMenu();
    // Smooth scroll to section
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <LandingRoot>
      {/* Navigation */}
      <Navbar>
        <Logo>
          <LogoIcon src="/dermAI_logo.svg" alt="DermAfricans Logo" />
        </Logo>
        
        {/* Desktop Navigation */}
        <NavLinks>
          <li><NavLink href="#features">Features</NavLink></li>
          <li><NavLink href="#testimonials">Testimonials</NavLink></li>
          <li><NavLink href="#about">About</NavLink></li>
          <li><LoginButton onClick={handleLogin}>Login</LoginButton></li>
          <li><GetStartedButton onClick={handleGetStarted}>Get Started</GetStartedButton></li>
        </NavLinks>
        
        {/* Mobile Menu Button */}
        <MobileMenuButton onClick={toggleMobileMenu}>
          ☰
        </MobileMenuButton>
      </Navbar>

      {/* Mobile Overlay */}
      <MobileOverlay isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

      {/* Mobile Side Navigation */}
      <MobileSideNav isOpen={isMobileMenuOpen}>
        <MobileCloseButton onClick={closeMobileMenu}>
          ✕
        </MobileCloseButton>
        
        <MobileNavLinks>
          <MobileNavLink 
            href="#features" 
            onClick={(e) => {
              e.preventDefault();
              handleMobileNavClick('#features');
            }}
          >
            Features
          </MobileNavLink>
          <MobileNavLink 
            href="#testimonials"
            onClick={(e) => {
              e.preventDefault();
              handleMobileNavClick('#testimonials');
            }}
          >
            Testimonials
          </MobileNavLink>
          <MobileNavLink 
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              handleMobileNavClick('#about');
            }}
          >
            About
          </MobileNavLink>
        </MobileNavLinks>
        
        <MobileButtonContainer>
          <LoginButton onClick={() => { closeMobileMenu(); handleLogin(); }}>
            Login
          </LoginButton>
          <GetStartedButton onClick={() => { closeMobileMenu(); handleGetStarted(); }}>
            Get Started
          </GetStartedButton>
        </MobileButtonContainer>
      </MobileSideNav>

      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroContent>
          <HeroText>
            <h1>
              Diagnose Skin Diseases <span className="highlight">Smarter</span> with AI
            </h1>
            <p>
              AI-powered dermatological diagnosis tool designed specifically for African healthcare professionals. 
              Upload a skin image and get instant, accurate analysis in seconds.
            </p>
            <GetStartedButton onClick={handleGetStarted}>
              Start Free Trial
            </GetStartedButton>
          </HeroText>
          <HeroImage>
            <img 
              src="https://images.unsplash.com/photo-1666886573553-6548db92db79?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
              alt="Healthcare Professional Using DermAfricans"
            />
          </HeroImage>
        </HeroContent>
      </HeroSection>

      {/* Features Section */}
      <Section id="features" ref={featuresRef}>
        <FloatingElements>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
          <div className="floating-circle"></div>
        </FloatingElements>
        
        <h2>How DermAfricans Works</h2>
        <FeaturesSubtitle>
          Experience the future of dermatological diagnosis with our cutting-edge AI technology
        </FeaturesSubtitle>
        
        <FeaturesContainer>
          <FeaturesGrid>
          <FeatureCard data-feature-card>
            <div className="feature-number">01</div>
            <div className="icon-container">
              <CameraIcon />
            </div>
            <h3>Smart Image Upload</h3>
            <p>Securely upload high-quality skin images from any device with our intelligent image processing system that automatically optimizes for diagnosis.</p>
          </FeatureCard>
          <FeatureCard data-feature-card>
            <div className="feature-number">02</div>
            <div className="icon-container">
              <BrainIcon />
            </div>
            <h3>Advanced AI Analysis</h3>
            <p>Our state-of-the-art neural networks, trained on diverse African skin tones, analyze images for 100+ dermatological conditions with 95% accuracy.</p>
          </FeatureCard>
          <FeatureCard data-feature-card>
            <div className="feature-number">03</div>
            <div className="icon-container">
              <ZapIcon />
            </div>
            <h3>Lightning Fast Results</h3>
            <p>Receive comprehensive diagnosis reports with confidence scores, differential diagnoses, and treatment recommendations in under 10 seconds.</p>
          </FeatureCard>
          <FeatureCard data-feature-card>
            <div className="feature-number">04</div>
            <div className="icon-container">
              <UserIcon />
            </div>
            <h3>Expert Consultation</h3>
            <p>Connect with board-certified dermatologists for complex cases requiring human expertise, second opinions, and specialized treatment plans.</p>
          </FeatureCard>
          <FeatureCard data-feature-card>
            <div className="feature-number">05</div>
            <div className="icon-container">
              <ShieldIcon />
            </div>
            <h3>Enterprise Security</h3>
            <p>Bank-level encryption, HIPAA compliance, and SOC 2 certification ensure patient data privacy and security at every step of the process.</p>
          </FeatureCard>
          <FeatureCard data-feature-card>
            <div className="feature-number">06</div>
            <div className="icon-container">
              <DevicesIcon />
            </div>
            <h3>Cross-Platform Ready</h3>
            <p>Seamlessly access DermAfricans from desktop, tablet, or mobile with our responsive PWA that works offline and syncs automatically.</p>
          </FeatureCard>
        </FeaturesGrid>
        </FeaturesContainer>
      </Section>

      {/* Testimonials Section */}
      <TestimonialSection id="testimonials">
        <h2>Trusted by Healthcare Professionals</h2>
        <TestimonialCard>
          <TestimonialAvatar 
            src={testimonials[currentTestimonial].avatar} 
            alt={testimonials[currentTestimonial].name}
          />
          <TestimonialText>
            "{testimonials[currentTestimonial].text}"
          </TestimonialText>
          <TestimonialAuthor>
            <strong>{testimonials[currentTestimonial].name}</strong><br />
            {testimonials[currentTestimonial].role}
          </TestimonialAuthor>
        </TestimonialCard>
      </TestimonialSection>

      {/* Call to Action */}
      <CTASection>
        <h2>Ready to Transform Dermatology Care?</h2>
        <p>Join thousands of healthcare professionals using DermAfricans to provide better patient care across Africa.</p>
        <CTAButton onClick={handleGetStarted}>
          Start Your Free Trial Today
        </CTAButton>
      </CTASection>

      {/* Footer */}
      <Footer>
        <div className="footer-content">
          <div className="footer-section">
            <Logo>
              <LogoIcon src="/dermAI_logo.svg" alt="DermAfricans Logo" />
            </Logo>
            <p>
              Empowering African healthcare professionals with AI-powered dermatological diagnosis tools.
            </p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <p><a href="#features">Features</a></p>
            <p><a href="#pricing">Pricing</a></p>
            <p><a href="#docs">Documentation</a></p>
            <p><a href="#api">API</a></p>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <p><a href="#about">About Us</a></p>
            <p><a href="#careers">Careers</a></p>
            <p><a href="#press">Press</a></p>
            <p><a href="#contact">Contact</a></p>
          </div>
          <div className="footer-section">
            <h4>Support</h4>
            <p><a href="#help">Help Center</a></p>
            <p><a href="#community">Community</a></p>
            <p><a href="#status">System Status</a></p>
            <p><a href="#privacy">Privacy Policy</a></p>
          </div>
        </div>
        <div className="footer-bottom">
          © {new Date().getFullYear()} DermAfricans. All rights reserved.
        </div>
      </Footer>
    </LandingRoot>
  );
}